(() => {
  if (customElements.get('coupon-quiz')) return;

  const sources = {
    google: 'Google',
    amazon: 'Amazon',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    friend: 'A friend',
    other: 'Other',
  };
  const goals = {
    blood_pressure: 'Healthy Blood Pressure',
    blood_flow: 'Blood Flow & Circulation',
    stamina: 'Stamina & Physical Resilience',
    energy: 'Everyday Energy & Vitality',
    mind_wont_switch_off: 'Mind Won’t Switch Off',
    morning_fog: 'Tired of Morning Fog',
    deep_sleep: 'Want a Deep Sleep',
    non_habit_sleep_aid: 'Non-habit forming Sleep Aid',
    softer_wind_down: 'Softer Wind-Down',
    bedtime_ritual: 'Healthy Bedtime Ritual',
    pressure: 'Healthy Blood Pressure & Circulation',
    tension: 'Easing Mental Tension',
    sleep: 'Deeper, Restful Sleep',
    exploring: 'Just Exploring',
  };
  const products = {
    nitric: 'Nitric Oxide Vital',
    calm: 'Calm & Ease',
    relax: 'Relax & Sleep',
    exploring: 'Just Exploring',
  };

  const AUTO_DISPLAY_DELAY = 5000;
  const CLOSE_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;
  const STORAGE_PREFIX = 'dobel-coupon-quiz:v1';
  const CLOSE_STORAGE_KEY = `${STORAGE_PREFIX}:closed-at`;
  const COMPLETED_STORAGE_KEY = `${STORAGE_PREFIX}:completed`;

  const availableStorages = () => {
    const storages = [];
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        if (window[name]) storages.push(window[name]);
      } catch (error) {
        // Storage can be blocked before it is accessed.
      }
    }
    return storages;
  };

  const getStoredValue = (key) => {
    for (const storage of availableStorages()) {
      try {
        const value = storage.getItem(key);
        if (value !== null) return value;
      } catch (error) {
        // Storage can be disabled by the browser or privacy settings.
      }
    }
    return null;
  };

  const setStoredValue = (key, value) => {
    for (const storage of availableStorages()) {
      try {
        storage.setItem(key, value);
        return;
      } catch (error) {
        // Try session storage when persistent storage is unavailable.
      }
    }
  };

  let turnstileLoadPromise;
  const loadTurnstile = () => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoadPromise) return turnstileLoadPromise;
    turnstileLoadPromise = new Promise((resolve, reject) => {
      let script = document.querySelector('script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.defer = true;
        script.addEventListener('error', () => reject(new Error('verification_unavailable')), { once: true });
        document.head.appendChild(script);
      }
      const startedAt = Date.now();
      const wait = () => {
        if (window.turnstile) return resolve(window.turnstile);
        if (Date.now() - startedAt > 8000) return reject(new Error('verification_timeout'));
        window.setTimeout(wait, 50);
      };
      wait();
    });
    return turnstileLoadPromise;
  };

  const createSubmissionId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  };

  class CouponQuiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.modal = this.querySelector('.coupon-modal');
      this.dialog = this.querySelector('.coupon-dialog');
      this.form = this.querySelector('form');
      this.endpoint = this.dataset.endpoint || '';
      this.siteKey = this.dataset.turnstileSiteKey || '';
      this.turnstileContainer = this.querySelector('[data-turnstile-container]');
      this.turnstileWidgetId = null;
      this.pendingTurnstile = null;
      this.turnstileToken = '';
      this.submitting = false;
      this.step = 1;
      this.completed = false;
      this.autoDisplayTimer = null;

      this.openControls = [...document.querySelectorAll('[data-coupon-open]')].filter(
        (control) => control.getAttribute('aria-controls') === this.modal.id,
      );
      const localControl = this.querySelector('.coupon-open');
      if (localControl) this.openControls.push(localControl);
      this.openControls.forEach((control) => control.addEventListener('click', () => this.open(control)));
      this.querySelectorAll('.coupon-close').forEach((control) => control.addEventListener('click', () => this.close()));
      this.querySelectorAll('.next').forEach((control) => control.addEventListener('click', () => this.next()));
      this.querySelectorAll('[name="source"]').forEach((control) => control.addEventListener('click', (event) => this.selectSource(event)));
      this.querySelectorAll('.back').forEach((control) => control.addEventListener('click', () => this.show(this.step - 1)));
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.form.addEventListener('change', (event) => this.change(event));
      this.form.addEventListener('input', () => this.clearError());
      this.form.addEventListener('keydown', (event) => this.formKeys(event));
      this.addEventListener('keydown', (event) => this.keys(event));
      this.scheduleAutoOpen();
    }

    scheduleAutoOpen() {
      if (this.dataset.autoDisplay !== 'true' || this.dataset.pageType === 'cart' || this.dataset.designMode === 'true' || this.isAutoDisplaySuppressed()) return;

      this.autoDisplayTimer = window.setTimeout(() => {
        this.autoDisplayTimer = null;
        if (document.hidden || !this.modal.hidden || document.querySelector('.coupon-modal:not([hidden])')) return;
        this.open();
      }, AUTO_DISPLAY_DELAY);
    }

    isAutoDisplaySuppressed() {
      if (getStoredValue(COMPLETED_STORAGE_KEY) === 'true') return true;
      const closedAt = Number(getStoredValue(CLOSE_STORAGE_KEY));
      return Number.isFinite(closedAt) && Date.now() - closedAt < CLOSE_COOLDOWN_MS;
    }

    recordDismissal() {
      setStoredValue(CLOSE_STORAGE_KEY, String(Date.now()));
    }

    recordCompletion() {
      this.completed = true;
      setStoredValue(COMPLETED_STORAGE_KEY, 'true');
    }

    open(trigger) {
      if (this.autoDisplayTimer !== null) {
        window.clearTimeout(this.autoDisplayTimer);
        this.autoDisplayTimer = null;
      }
      this.completed = false;
      this.reset();
      this.trigger = trigger;
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('coupon-open');
      requestAnimationFrame(() => this.dialog.focus());
    }

    close() {
      if (!this.completed) this.recordDismissal();
      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('coupon-open');
      this.reset();
      this.trigger?.focus();
    }

    reset() {
      this.form.reset();
      this.querySelectorAll('.goal').forEach((goal) => {
        goal.classList.remove('is-selected');
        const checkIcon = goal.querySelector('.coupon-choice-check');
        if (checkIcon) checkIcon.hidden = true;
      });
      this.submissionId = createSubmissionId();
      this.querySelector('.source-other').hidden = true;
      this.turnstileToken = '';
      this.pendingTurnstile = null;
      if (this.turnstileContainer) this.turnstileContainer.hidden = true;
      if (this.turnstileWidgetId !== null && window.turnstile) window.turnstile.reset(this.turnstileWidgetId);
      this.show(1, false);
    }

    show(step, focus = true) {
      this.step = step;
      this.dialog.dataset.step = String(step);
      this.querySelectorAll('.coupon-step').forEach((section) => {
        section.hidden = section.dataset.step !== String(step);
      });

      const activeStep = this.querySelector(`[data-step="${step}"]`);
      const activeTitle = activeStep?.querySelector('h2');
      if (activeTitle?.id) this.dialog.setAttribute('aria-labelledby', activeTitle.id);

      this.clearError();
      if (focus) requestAnimationFrame(() => activeTitle?.focus());
      this.querySelector('.coupon-main').scrollTo({ top: 0, behavior: 'smooth' });
      if (step === 3) this.getTurnstileToken().catch(() => {});
    }

    next() {
      if (this.valid()) this.show(Math.min(3, this.step + 1));
    }

    valid() {
      const fields = this.form.elements;
      if (this.step === 1 && !this.goalValues().length) {
        return this.error('Choose at least one wellness goal.');
      }
      if (this.step === 2) {
        if (!fields.source.value) return this.error('Choose how you found Dobel.');
        if (fields.source.value === 'other' && !fields.sourceOther.value.trim()) {
          return this.error('Tell us where you found Dobel.', fields.sourceOther);
        }
      }
      if (this.step === 3) {
        if (!fields.email.value.trim()) return this.error('Enter your email address.', fields.email);
        if (!fields.email.validity.valid) return this.error('Enter a valid email address.', fields.email);
      }
      return true;
    }

    error(message, field) {
      const error = this.querySelector(`[data-step="${this.step}"] .coupon-error`);
      error.textContent = message;
      error.hidden = false;
      field?.focus();
      return false;
    }

    clearError() {
      this.querySelectorAll('.coupon-error').forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
    }

    selectSource(event) {
      const other = this.querySelector('.source-other');
      other.hidden = event.target.value !== 'other';
      if (!other.hidden) requestAnimationFrame(() => this.form.elements.sourceOther.focus());
      else requestAnimationFrame(() => this.show(3));
    }

    change(event) {

      if (event.target.name === 'sourceOther') {
        if (event.target.value.trim()) this.show(3);
        else this.error('Tell us where you found Dobel.', event.target);
        return;
      }

      if (event.target.name !== 'goals') return;
      const boxes = [...this.form.querySelectorAll('[name="goals"]')];

      if (boxes.filter((box) => box.checked).length > 3) {
        event.target.checked = false;
        this.error('Choose up to three wellness goals.');
      }
      boxes.forEach((box) => {
        const goal = box.closest('.goal');
        goal?.classList.toggle('is-selected', box.checked);
        const checkIcon = goal?.querySelector('.coupon-choice-check');
        if (checkIcon) checkIcon.hidden = !box.checked;
      });
    }

    goalValues() {
      return [...this.form.querySelectorAll('[name="goals"]:checked')].map((input) => input.value);
    }

    async submit(event) {
      event.preventDefault();
      if (this.submitting || !this.valid()) return;

      const data = new FormData(this.form);
      const source = String(data.get('source') || '');
      const email = String(data.get('email') || '').trim();
      const phone = String(data.get('phone') || '').trim();
      const submitButton = event.submitter || this.form.querySelector('[type="submit"]');
      const submitLabel = submitButton.textContent;
      this.submitting = true;
      submitButton.disabled = true;
      submitButton.textContent = 'Verifying...';

      try {
        if (!this.endpoint || !this.siteKey || !this.turnstileContainer) throw new Error('coupon_not_configured');
        const turnstileToken = await this.getTurnstileToken();
        submitButton.textContent = 'Saving...';
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            submissionId: this.submissionId,
            goals: this.goalValues(),
            productKey: String(data.get('product') || ''),
            source,
            sourceDetail: String(data.get('sourceOther') || '').trim(),
            email,
            phone,
            couponCode: this.dataset.code || '',
            privacyNoticeShown: true,
            sourcePage: `${window.location.origin}${window.location.pathname}`,
            turnstileToken,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.ok !== true) throw new Error(result.code || 'coupon_save_failed');

        this.summary('source', source === 'other' ? `Other: ${String(data.get('sourceOther')).trim()}` : sources[source]);
        this.summary('contact', phone ? `${email} · ${phone}` : email);
        this.summary('goals', this.goalValues().map((value) => goals[value]).join(', '));
        this.summary('product', products[data.get('product')]);
        this.recordCompletion();
        this.show('success');
      } catch (error) {
        console.error('Coupon submission failed', String(error?.message || error).slice(0, 80));
        this.error('We could not save your details. Please try again.');
      } finally {
        this.submitting = false;
        submitButton.disabled = false;
        submitButton.textContent = submitLabel;
      }
    }

    async getTurnstileToken() {
      if (this.turnstileToken) return this.turnstileToken;
      if (this.pendingTurnstile) return this.pendingTurnstile;
      const turnstile = await loadTurnstile();
      this.turnstileContainer.hidden = false;
      this.pendingTurnstile = new Promise((resolve, reject) => {
        let timeoutId;
        const complete = (callback, value) => {
          window.clearTimeout(timeoutId);
          this.pendingTurnstile = null;
          this.turnstileContainer.hidden = true;
          callback(value);
        };
        const options = {
          sitekey: this.siteKey,
          action: 'coupon_submission',
          appearance: 'interaction-only',
          callback: (token) => { this.turnstileToken = token; complete(resolve, token); },
          'error-callback': (code) => complete(reject, new Error('verification_failed_' + (code || 'unknown'))),
          'expired-callback': () => { this.turnstileToken = ''; },
          'timeout-callback': () => complete(reject, new Error('verification_timeout')),
        };
        if (this.turnstileWidgetId === null) this.turnstileWidgetId = turnstile.render(this.turnstileContainer, options);
        else turnstile.reset(this.turnstileWidgetId);
        timeoutId = window.setTimeout(() => complete(reject, new Error('verification_timeout')), 90000);
      });
      return this.pendingTurnstile;
    }
    summary(key, value) {
      this.querySelector(`[data-summary="${key}"]`).textContent = value;
    }


    formKeys(event) {
      if (event.key !== 'Enter' || event.target.name !== 'sourceOther') return;
      event.preventDefault();
      if (!event.target.value.trim()) {
        this.error('Tell us where you found Dobel.', event.target);
        return;
      }
      this.show(3);
    }

    keys(event) {
      if (this.modal.hidden) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((control) => !control.closest('[hidden]'));
      if (!controls.length) return;
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('coupon-quiz', CouponQuiz);
})();